import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Sparkles, Crown, Check } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SHOP } from "@/constants/testIds";
import CardFace from "@/components/CardFace";
import SoulMarkCard from "@/components/SoulMarkCard";

/**
 * The AuraVeil Shop — luxury product gallery for the three physical editions.
 *
 * Every product uses the SAME architectural logic:
 *   ▸ Faces = 78 proprietary AuraVeil CardFace SVGs (standard across all decks)
 *   ▸ Back  = the seeker's unique AI-generated Soul Mark
 * Tiers differ only in physical craftsmanship (stock, finish, packaging).
 */
export default function Shop() {
  const [editions, setEditions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [reserving, setReserving] = useState(null); // founder edition or null
  const [shipping, setShipping] = useState({
    name: "",
    address: "",
    city: "",
    postal: "",
    country: "",
  });
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  const refreshEditions = () =>
    api.get("/commerce/editions").then((r) => setEditions(r.data.editions)).catch(() => {});

  useEffect(() => {
    refreshEditions();
    api.get("/commerce/orders").then((r) => setOrders(r.data.orders || [])).catch(() => {});
  }, []);

  const openTier = (edition) => {
    if (edition.id === "founder") {
      setReserving(edition);
    } else {
      setCheckout(edition);
    }
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/commerce/checkout", {
        edition_id: checkout.id,
        shipping,
      });
      toast.success(data.message);
      setPlacedOrder(data.order);
      setCheckout(null);
      setShipping({ name: "", address: "", city: "", postal: "", country: "" });
      // Refresh editions (so the Founder LIMITED remaining count updates live)
      // and orders in parallel.
      Promise.all([
        api.get("/commerce/editions").then((r) => setEditions(r.data.editions)).catch(() => {}),
        api.get("/commerce/orders").then((r) => setOrders(r.data.orders || [])).catch(() => {}),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The ritual purchase failed.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div
      data-testid={SHOP.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="starfield-subtle" />
      <div className="relative max-w-6xl mx-auto">
        <div className="mb-16">
          <div className="font-overline text-[#C8A96E] mb-4">The Shop</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
            Bind your deck
            <br />
            into the physical world.
          </h1>
          <p className="mt-6 text-lg text-[#F4EFE6]/60 max-w-2xl leading-relaxed">
            Every AuraVeil deck ships with the same seventy-eight proprietary
            faces — and your one-of-one <em className="text-[#C8A96E] not-italic">Soul Mark</em> on
            every back. Choose the craftsmanship you want carrying it.
          </p>
        </div>

        {/* Soul Mark — the generated part */}
        <div className="mb-16">
          <SoulMarkCard />
        </div>

        {/* Product tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {editions.map((e) => (
            <TierCard
              key={e.id}
              edition={e}
              onOrder={() => openTier(e)}
              disabled={e.id === "founder" && !e.available}
            />
          ))}
        </div>

        {/* Orders */}
        {orders.length > 0 && (
          <div className="mt-20">
            <div className="font-overline text-[#C8A96E] mb-6">Your rituals</div>
            <div className="grid gap-3">
              {orders.map((o) => (
                <div
                  key={o.order_id}
                  className="card-frame p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="font-heading italic text-lg text-[#F4EFE6]">
                      {o.edition_name}
                      {o.founder_number && (
                        <span className="text-[#C8A96E] not-italic ml-3 text-sm">
                          № {String(o.founder_number).padStart(3, "0")} / 500
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#F4EFE6]/50 mt-1">
                      {new Date(o.created_at).toLocaleDateString()} · ${o.price_usd}
                    </div>
                  </div>
                  <div className="font-overline text-[10px] text-[#C8A96E]">
                    {o.status === "paid_mock" ? "PAID · MOCK" : o.status.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Checkout modal (standard + luxury only) */}
      <AnimatePresence>
        {checkout && (
          <CheckoutModal
            edition={checkout}
            shipping={shipping}
            setShipping={setShipping}
            onCancel={() => setCheckout(null)}
            onSubmit={placeOrder}
            placing={placing}
          />
        )}
      </AnimatePresence>

      {/* Reservation sheet (Founder Edition — no payment) */}
      <AnimatePresence>
        {reserving && (
          <ReservationSheet
            edition={reserving}
            onClose={() => {
              setReserving(null);
              refreshEditions();
            }}
          />
        )}
      </AnimatePresence>

      {/* Success confirmation */}
      <AnimatePresence>
        {placedOrder && (
          <SuccessModal order={placedOrder} onClose={() => setPlacedOrder(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Tier card — a luxury product tile ────────────────────────────────
function TierCard({ edition, onOrder, disabled }) {
  const isFounder = edition.id === "founder";
  const isLuxury = edition.id === "luxury";
  return (
    <motion.div
      data-testid={SHOP.tierCard(edition.id)}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="relative rounded-2xl overflow-hidden flex flex-col"
      style={{
        background:
          "linear-gradient(180deg, rgba(20,17,10,0.6) 0%, rgba(8,8,8,0.9) 100%)",
        border: isFounder
          ? "1px solid rgba(212,175,55,0.55)"
          : "1px solid rgba(212,175,55,0.22)",
        boxShadow: isFounder
          ? "0 0 60px rgba(212,175,55,0.18), 0 32px 80px rgba(0,0,0,0.55)"
          : "0 22px 60px rgba(0,0,0,0.55)",
      }}
    >
      {isFounder && (
        <div
          className="absolute top-0 right-0 px-3 py-1.5 rounded-bl-xl font-overline text-[10px] tracking-[0.24em]"
          style={{
            background: "linear-gradient(135deg, #D4AF37, #C8A96E)",
            color: "#0B0B0B",
          }}
        >
          LIMITED · {edition.remaining ?? 500} / 500
        </div>
      )}

      {/* Visual card mock (proprietary face preview) */}
      <div
        className="relative flex items-center justify-center py-10 border-b"
        style={{
          borderColor: "rgba(212,175,55,0.15)",
          background:
            "radial-gradient(circle at center, rgba(212,175,55,0.06), transparent 65%)",
        }}
      >
        <div
          className="relative"
          style={{
            transform: isLuxury
              ? "translateY(0) rotate(-6deg)"
              : isFounder
              ? "translateY(0) rotate(6deg)"
              : "translateY(0)",
          }}
        >
          <CardFace
            title={
              isFounder
                ? "The Sun"
                : isLuxury
                ? "The Star"
                : "The Magician"
            }
            size={140}
            style={
              isLuxury
                ? {
                    boxShadow:
                      "0 0 30px rgba(212,175,55,0.35), 0 22px 40px rgba(0,0,0,0.5)",
                    border: "1px solid rgba(212,175,55,0.6)",
                  }
                : isFounder
                ? {
                    boxShadow:
                      "0 0 40px rgba(212,175,55,0.45), 0 22px 40px rgba(0,0,0,0.55)",
                    border: "2px solid #D4AF37",
                  }
                : {}
            }
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-6">
        <div className="mb-2">
          {isFounder && (
            <div className="inline-flex items-center gap-1.5 font-overline text-[10px] text-[#C8A96E] mb-2">
              <Crown size={12} strokeWidth={1.5} />
              Founder
            </div>
          )}
        </div>
        <div className="font-heading italic text-2xl text-[#F4EFE6] mb-1">
          {edition.name}
        </div>
        <div className="font-heading text-3xl text-[#C8A96E] mb-4">
          ${Number.isInteger(edition.price_usd) ? edition.price_usd : edition.price_usd.toFixed(2)}
        </div>
        {isFounder && (
          <div className="mb-4 -mt-2">
            <div
              className="font-heading italic"
              style={{ color: "#C8A96E", fontSize: 15, lineHeight: 1.4 }}
            >
              ${edition.reservation_price_usd ?? 314} for reserved numbers — 10% below release.
            </div>
            <div
              data-testid={SHOP.reserveRemaining}
              className="mt-1"
              style={{
                color: "rgba(244,239,230,0.55)",
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: 12,
                letterSpacing: "0.02em",
              }}
            >
              {edition.remaining ?? 500} of 500 numbers left.
            </div>
          </div>
        )}
        <p className="text-sm text-[#F4EFE6]/60 leading-relaxed mb-5 flex-1">
          {edition.description}
        </p>
        <ul className="space-y-2 mb-6">
          {edition.perks.map((p, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-[#F4EFE6]/75"
            >
              <Check
                size={13}
                strokeWidth={2}
                className="mt-1 shrink-0"
                style={{ color: "#D4AF37" }}
              />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <button
          data-testid={SHOP.tierOrderBtn(edition.id)}
          onClick={onOrder}
          disabled={disabled}
          className={
            isFounder
              ? "btn-sacred btn-sacred-solid disabled:opacity-40 disabled:cursor-not-allowed"
              : "btn-sacred disabled:opacity-40 disabled:cursor-not-allowed"
          }
        >
          {disabled
            ? "Sold out"
            : isFounder
            ? "Reserve my number"
            : "Bind this Edition"}
        </button>
      </div>
    </motion.div>
  );
}

// ─── Checkout modal ────────────────────────────────────────────────────
function CheckoutModal({ edition, shipping, setShipping, onCancel, onSubmit, placing }) {
  const setField = (k, v) => setShipping((prev) => ({ ...prev, [k]: v }));
  const canSubmit =
    shipping.name && shipping.address && shipping.city && shipping.postal && shipping.country;
  return (
    <motion.div
      data-testid={SHOP.checkoutModal}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: "rgba(4,4,4,0.85)", backdropFilter: "blur(12px)" }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 20 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-lg rounded-2xl p-6 sm:p-8"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.35)",
          boxShadow: "0 40px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-overline text-[#C8A96E] mb-1">Checkout</div>
            <div className="font-heading italic text-2xl text-[#F4EFE6]">
              {edition.name}
            </div>
            <div className="font-heading text-xl text-[#C8A96E] mt-1">
              ${Number.isInteger(edition.price_usd) ? edition.price_usd : edition.price_usd.toFixed(2)}
            </div>
          </div>
          <button
            data-testid={SHOP.checkoutCancel}
            onClick={onCancel}
            className="h-8 w-8 rounded-full flex items-center justify-center transition-colors"
            style={{ border: "1px solid rgba(212,175,55,0.3)", color: "#D4AF37" }}
            aria-label="Cancel checkout"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>

        <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.18)" }}>
          <div className="font-overline text-[10px] text-[#C8A96E] mb-1">Payment · Mock</div>
          <div className="text-xs text-[#F4EFE6]/70">
            No card will be charged. This is a ceremonial preview flow.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            data-testid={SHOP.checkoutName}
            className="input-sacred sm:col-span-2"
            placeholder="Full name"
            value={shipping.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          <input
            data-testid={SHOP.checkoutAddress}
            className="input-sacred sm:col-span-2"
            placeholder="Street address"
            value={shipping.address}
            onChange={(e) => setField("address", e.target.value)}
          />
          <input
            data-testid={SHOP.checkoutCity}
            className="input-sacred"
            placeholder="City"
            value={shipping.city}
            onChange={(e) => setField("city", e.target.value)}
          />
          <input
            data-testid={SHOP.checkoutPostal}
            className="input-sacred"
            placeholder="Postal code"
            value={shipping.postal}
            onChange={(e) => setField("postal", e.target.value)}
          />
          <input
            data-testid={SHOP.checkoutCountry}
            className="input-sacred sm:col-span-2"
            placeholder="Country"
            value={shipping.country}
            onChange={(e) => setField("country", e.target.value)}
          />
        </div>

        <button
          data-testid={SHOP.checkoutSubmit}
          onClick={onSubmit}
          disabled={!canSubmit || placing}
          className="btn-sacred btn-sacred-solid mt-6 w-full disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {placing ? "Sealing…" : "Complete the ritual"}
        </button>
      </motion.div>
    </motion.div>
  );
}

function SuccessModal({ order, onClose }) {
  return (
    <motion.div
      data-testid={SHOP.orderSuccess}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: "rgba(4,4,4,0.85)", backdropFilter: "blur(12px)" }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 20 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md rounded-2xl p-8 text-center"
        style={{
          background: "#0B0B0B",
          border: "1px solid rgba(212,175,55,0.4)",
          boxShadow: "0 0 60px rgba(212,175,55,0.2), 0 40px 80px rgba(0,0,0,0.7)",
        }}
      >
        <Sparkles size={28} strokeWidth={1.5} className="mx-auto mb-4" style={{ color: "#D4AF37" }} />
        <div className="font-heading italic text-2xl sm:text-3xl text-[#F4EFE6] mb-3">
          The ritual is sealed.
        </div>
        {order.founder_number && (
          <div className="font-heading text-lg text-[#C8A96E] mb-2">
            Founder № {String(order.founder_number).padStart(3, "0")} / 500
          </div>
        )}
        <p className="text-sm text-[#F4EFE6]/65 mb-6 leading-relaxed">
          {order.edition_name} bound to your soul record. Your Soul Mark will
          adorn every card back. Watch your Journal for the crafting ceremony.
        </p>
        <button onClick={onClose} className="btn-sacred">Return to the shop</button>
      </motion.div>
    </motion.div>
  );
}

// ─── Reservation Sheet (Founder Edition — no payment) ──────────────────
function ReservationSheet({ edition, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { number, existing }

  const canSubmit = /\S+@\S+\.\S+/.test(email) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/commerce/reservations", {
        email: email.trim().toLowerCase(),
      });
      setResult({ number: data.number, existing: data.existing });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not reserve your number.",
      );
    } finally {
      setBusy(false);
    }
  };

  const price = edition.reservation_price_usd ?? 314;

  return (
    <motion.div
      data-testid={SHOP.reserveSheet}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:px-4"
      style={{ background: "rgba(4,4,4,0.85)", backdropFilter: "blur(12px)" }}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-6 sm:p-8"
        style={{
          background: "#0B0E1A",
          border: "1px solid rgba(200,169,110,0.4)",
          boxShadow:
            "0 0 60px rgba(200,169,110,0.14), 0 40px 80px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1 pr-4">
            {result ? (
              <div
                className="font-overline mb-1"
                style={{ color: "#C8A96E" }}
              >
                Founder · Reserved
              </div>
            ) : (
              <div
                className="font-overline mb-1"
                style={{ color: "#C8A96E" }}
              >
                Founder · Reservation
              </div>
            )}
            {!result && (
              <div
                className="font-heading text-2xl sm:text-3xl"
                style={{ color: "#F4EFE6", fontWeight: 300 }}
              >
                Reserve your Founder number
              </div>
            )}
          </div>
          <button
            data-testid={SHOP.reserveCancel}
            onClick={onClose}
            className="h-10 w-10 rounded-full flex items-center justify-center transition-colors"
            style={{
              border: "1px solid rgba(200,169,110,0.3)",
              color: "#C8A96E",
              minWidth: 44,
              minHeight: 44,
            }}
            aria-label="Close reservation"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {!result ? (
          <>
            <p
              className="mb-4"
              style={{
                color: "#F4EFE6",
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: 15,
                lineHeight: 1.65,
                opacity: 0.85,
              }}
            >
              500 decks, numbered and signed, in a silk-lined case with
              lifetime access. Claim your number now and hold it at ${price}.
            </p>
            <p
              className="mb-6"
              style={{
                color: "rgba(244,239,230,0.55)",
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              No payment today. Once all 500 numbers are claimed, we come to
              you — and you decide then.
            </p>

            <label
              className="font-overline block mb-2"
              style={{ color: "rgba(244,239,230,0.7)" }}
            >
              Email
            </label>
            <input
              data-testid={SHOP.reserveEmail}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@sanctuary.io"
              className="input-sacred w-full"
              style={{ minHeight: 44 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />

            <button
              data-testid={SHOP.reserveSubmit}
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="btn-sacred btn-sacred-solid mt-6 w-full disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ minHeight: 48 }}
            >
              {busy ? "Reserving…" : "Reserve"}
            </button>
          </>
        ) : (
          <div data-testid={SHOP.reserveSuccess}>
            <div
              className="font-heading italic mb-3"
              style={{
                color: "#F4EFE6",
                fontSize: 28,
                lineHeight: 1.25,
                fontWeight: 300,
              }}
            >
              Welcome. You&apos;re number{" "}
              <span style={{ color: "#C8A96E", fontStyle: "normal" }}>
                {String(result.number).padStart(3, "0")}
              </span>{" "}
              of 500.
            </div>
            <p
              className="mb-8"
              style={{
                color: "rgba(244,239,230,0.55)",
                fontFamily: '"DM Sans", system-ui, sans-serif',
                fontSize: 13,
                lineHeight: 1.65,
              }}
            >
              We&apos;ll be in touch when the 500 are claimed. Nothing is owed
              by either of us until then.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-sacred w-full"
              style={{ minHeight: 44 }}
            >
              Return to the shop
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

